import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { AxiosError } from 'axios';
import { z } from 'zod';

import Button from '../../common/Button';
import { toastPromise } from '../../common/utils';
import { useUpdateModel } from '../../hooks';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import FormInput from '../form/FormInput';

const RetirementAgeConfigurationSchema = z.object({
  label: z.string(),
  value: z.string(),
});

type RetirementAgeConfigurationSchemaType = z.infer<
  typeof RetirementAgeConfigurationSchema
>;

const RetirementAgeConfiguration = () => {
  const { modelId, orgId, projectId } = useGetRouteParams();

  const [openSelectDatasetDialog, setOpenSelectDatasetDialog] = useState(false);

  const form = useForm<RetirementAgeConfigurationSchemaType>({
    resolver: zodResolver(RetirementAgeConfigurationSchema),
    defaultValues: {
      value: '',
      label: '',
    },
  });

  const { handleSubmit, setValue, watch, reset, setError } = form;

  const { model, isDefault, isRunningModelOutputServer } =
    useGetDistinguishModel();

  const { mutateAsync: updateModel, isPending: isUpdatingModel } =
    useUpdateModel({
      onSuccess: () => {
        handleCloseDatasetDialog();
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response) {
          setError('value', {
            message: error.response['data']['retirement_age'][0],
          });
        }
      },
    });

  useEffect(() => {
    if (model) {
      const value = model.retirement_age.toString();

      reset({
        label: value,
        value,
      });
    }
  }, [model]);

  const handleCloseDatasetDialog = () => {
    setOpenSelectDatasetDialog(false);
  };

  const handleOpenDatasetDialog = () => {
    setOpenSelectDatasetDialog(true);
  };

  const handleOnSubmit = () => {
    const { value } = watch();

    if (!model) {
      return;
    }

    if (!value) {
      setError('value', {
        message: 'This field is required',
      });
      return;
    } else if (Number(value) < 0) {
      setError('value', {
        message: 'Value should be greater than 0',
      });
      return;
    }

    const res = updateModel({
      orgId,
      projectId,
      modelId,
      data: {
        retirement_age: Number(value),
        dataset: model.dataset.id,
      },
    });

    res.then(() => {
      setValue('label', value);
    });

    toastPromise({
      promise: res,
      content: 'Retirement age updated successfully',
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold">Retirement Age</h1>
            {!model?.is_base && !model?.is_locked && isDefault && (
              <Button
                variant="outlined"
                style={{
                  color: '#B8341B',
                  borderColor: '#B8341B',
                }}
                disabled={isRunningModelOutputServer}
                onClick={handleOpenDatasetDialog}
              >
                Edit
              </Button>
            )}
          </div>
          <div className="w-[30%]">
            <FormInput disabled name={'label'} />
          </div>
        </div>
        <Dialog
          maxWidth="sm"
          fullWidth
          open={openSelectDatasetDialog}
          onClose={handleCloseDatasetDialog}
        >
          <DialogTitle style={{ cursor: 'move' }}>
            <h2 className="font-bold">Edit retirement age</h2>
          </DialogTitle>
          <DialogContent>
            <div className="w-full">
              <FormInput
                placeholder="Enter retirement age"
                type="number"
                name="value"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex gap-3 px-4 pb-3">
              <Button
                autoFocus
                variant="outlined"
                type="button"
                disabled={isUpdatingModel}
                onClick={handleCloseDatasetDialog}
                tabIndex={3}
              >
                Cancel
              </Button>
              <Button
                autoFocus
                disabled={isUpdatingModel}
                loading={isUpdatingModel}
                onClick={handleOnSubmit}
                tabIndex={3}
                variant="contained_blue"
              >
                Save
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
};

export default RetirementAgeConfiguration;
